// Generic minimax AI used by additional engine modules.
// Each engine passes its own `applyMove` / `getLegalMoves` / static evaluator.

export function chooseMoveMinimax({
  state,
  applyMove,
  getLegalMoves,
  evaluate,
  difficulty = "intermediate",
  perspective,
}) {
  if (state.status !== "ACTIVE") return null;
  perspective = perspective || state.currentPlayer;

  if (difficulty === "beginner") {
    const moves = getLegalMoves(state);
    if (moves.length === 0) return null;
    const cap = moves.filter((m) => {
      try {
        const { state: next } = applyMove(state, m[0], m[1]);
        return next.captures[perspective] > state.captures[perspective];
      } catch { return false; }
    });
    if (cap.length > 0 && Math.random() < 0.7) return cap[Math.floor(Math.random() * cap.length)];
    return moves[Math.floor(Math.random() * moves.length)];
  }

  const depth = difficulty === "advanced" ? 5 : 3;
  const start = Date.now();
  const SOFT_MS = difficulty === "advanced" ? 800 : 250;

  function order(state, moves) {
    return moves
      .map((m) => {
        try {
          const { state: next } = applyMove(state, m[0], m[1]);
          return { m, gain: next.captures[state.currentPlayer] - state.captures[state.currentPlayer] };
        } catch { return { m, gain: -1 }; }
      })
      .sort((a, b) => b.gain - a.gain)
      .map((s) => s.m);
  }

  function mm(state, d, alpha, beta, maxing) {
    if (d === 0 || state.status !== "ACTIVE" || Date.now() - start > SOFT_MS) {
      return { score: evaluate(state, perspective), move: null };
    }
    const moves = getLegalMoves(state);
    if (moves.length === 0) return { score: evaluate(state, perspective), move: null };
    const ordered = order(state, moves);
    let best = ordered[0];
    if (maxing) {
      let v = -Infinity;
      for (const m of ordered) {
        const { state: nxt } = applyMove(state, m[0], m[1]);
        const { score } = mm(nxt, d - 1, alpha, beta, false);
        if (score > v) { v = score; best = m; }
        alpha = Math.max(alpha, v);
        if (beta <= alpha) break;
      }
      return { score: v, move: best };
    } else {
      let v = Infinity;
      for (const m of ordered) {
        const { state: nxt } = applyMove(state, m[0], m[1]);
        const { score } = mm(nxt, d - 1, alpha, beta, true);
        if (score < v) { v = score; best = m; }
        beta = Math.min(beta, v);
        if (beta <= alpha) break;
      }
      return { score: v, move: best };
    }
  }

  const { move } = mm(state, depth, -Infinity, Infinity, true);
  return move;
}
